import { z } from "zod";
import { fetchApi } from "@/lib/api-helpers";

// VGS 3DS Initialize Request/Response
export const vgsInitialize3DSRequestSchema = z.object({
  vgs_card_id: z.string(),
});

export const vgsInitialize3DSResponseSchema = z.object({
  tds_method_content: z.string().nullable().optional(),
  merchant_tx_id: z.string().optional(),
  three_ds_xid: z.string().optional(),

  three_ds_required: z.boolean().optional(),
  fingerprinting_required: z.boolean().optional(),
  skip_reason: z.string().optional(),
  message: z.string().optional(),
});

export type VgsInitialize3DSResponse = z.infer<
  typeof vgsInitialize3DSResponseSchema
>;

// VGS 3DS Confirm Request/Response
export const vgsConfirm3DSRequestSchema = z.object({
  transaction_id: z.string(),
});

export const vgsConfirm3DSResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  cart: z.any().optional(),
});

export type VgsConfirm3DSResponse = z.infer<typeof vgsConfirm3DSResponseSchema>;

// Initialize VGS 3DS (device fingerprinting)
export async function initializeVgs3DS({
  cartToken,
  vgsCardId,
}: {
  cartToken: string;
  vgsCardId: string;
}): Promise<VgsInitialize3DSResponse> {
  return fetchApi({
    endpoint: `/carts/${cartToken}/3ds/initialize`,
    method: "POST",
    input: { vgs_card_id: vgsCardId },
    inputSchema: vgsInitialize3DSRequestSchema,
    outputSchema: vgsInitialize3DSResponseSchema,
  });
}

// VGS 3DS Cancel Request/Response
export const vgsCancel3DSRequestSchema = z.object({
  merchant_tx_id: z.string().min(1),
});

export const vgsCancel3DSResponseSchema = z.object({
  canceled: z.boolean().optional(),
  state: z.string().optional(),
});

export type VgsCancel3DSResponse = z.infer<typeof vgsCancel3DSResponseSchema>;

// Cancel a customer-abandoned VGS 3DS challenge attempt.
// Attempt-scoped: the backend no-ops unless merchant_tx_id matches the
// cart's active attempt, so a stale tab can never cancel a newer attempt.
// The 5s abort keeps the Pay button from being stranded behind a hung
// request — on any failure the caller falls back to a local-only close
// and the backend safety net cleans the attempt up at T+6 regardless.
export async function cancelVgs3DS({
  cartToken,
  merchantTxId,
}: {
  cartToken: string;
  merchantTxId: string;
}): Promise<VgsCancel3DSResponse> {
  return fetchApi({
    endpoint: `/carts/${cartToken}/3ds/cancel`,
    method: "POST",
    input: { merchant_tx_id: merchantTxId },
    inputSchema: vgsCancel3DSRequestSchema,
    outputSchema: vgsCancel3DSResponseSchema,
    fetchOptions: { signal: AbortSignal.timeout(5_000) },
  });
}

// Confirm VGS 3DS completion
export async function confirmVgs3DS({
  cartToken,
  transactionId,
}: {
  cartToken: string;
  transactionId: string;
}): Promise<VgsConfirm3DSResponse> {
  return fetchApi({
    endpoint: `/carts/${cartToken}/confirm_3ds_vgs`,
    method: "POST",
    input: { transaction_id: transactionId },
    inputSchema: vgsConfirm3DSRequestSchema,
    outputSchema: vgsConfirm3DSResponseSchema,
  });
}
